import React, { Fragment, useState, useEffect } from "react";
import Topbar from "../layouts/TopBar";
import UserResources from "../resources/UserResources";
import RotaResources from "../resources/RotaResources";
import LocationResources from "../resources/LocationResources";
import EventResources from "../resources/EventResources";
import AbsenceResources from "../resources/AbsenceResources";
import ConfigResources from "../resources/ConfigResources";
import { logout } from "../redux/actions/LoggedInAction";
import { connect } from "react-redux";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import Select from 'react-select';
import moment from "moment";
import {ACCESS_DENIED} from '../Messages'
import toast from 'react-hot-toast'
import * as XLSX from 'xlsx'

function ManageRota({isLoggedIn, loggedInUser, logout}){

    const [viewType, setViewType] = useState('monthly')
    const [rota, setRota] = useState(null)
    const [originalRota, setOriginalRota] = useState(null)
    const [rotaDates, setRotaDates] = useState([])
    const [events, setEvents] = useState([])
    const [locations, setLocations] = useState([])
    const [selectedLocation, setSelectedLocation] = useState(null)
    const [selectedMonth, setSelectedMonth] = useState(null)
    const [selectedEvent, setSelectedEvent] = useState(null)
    const [availableMonths, setAvailableMonths] = useState([])
    const [monthYear, setMonthYear] = useState({ month: '', year: '', monthName: '' })
    const [users, setUsers] = useState([])
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [isRotaEnabled, setIsRotaEnabled] = useState(false)
    const [rotaRoles, setRotaRoles] = useState([])
    const [roleNameToIdMap, setRoleNameToIdMap] = useState({})
    const [isPublishing, setIsPublishing] = useState(false)
    const [isUnpublishing, setIsUnpublishing] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [absences, setAbsences] = useState([])
    const [photographyTeamIds, setPhotographyTeamIds] = useState([])

    let rota_resources = new RotaResources()
    const locationResources = new LocationResources()
    const userResources = new UserResources()
    const eventResources = new EventResources()
    const absenceResources = new AbsenceResources()
    const configResources = new ConfigResources()

    const loadMonthlyRota = (date, locationId) => {
        return rota_resources.get_monthly_rota(date, locationId)
    }

    const loadEventRota = (eventId) => {
        return rota_resources.get_event_rota_admin(eventId)
    }

    const loadRotaDates = () => {
        return rota_resources.get_rota_dates()
    }

    const loadEventRotas = () => {
        return rota_resources.get_event_rotas_admin()
    }

    const loadLocations = () => {
        return locationResources.get()
    }

    const loadUsers = () => {
        return userResources.get_admin()
    }

    const loadRotaRoles = () => {
        return rota_resources.get_rota_roles()
    }

    const handleViewTypeChange = (e) => {
        setViewType(e.target.value)
        setRota(null)
        setSelectedMonth(null)
        setSelectedLocation(null)
        setSelectedEvent(null)
        setHasUnsavedChanges(false)
        setIsRotaEnabled(false)
    }

    const handleMonthSelection = (selectedOption) => {
        setSelectedMonth(selectedOption)
    }

    const handleLocationSelection = (selectedOption) => {
        setSelectedLocation(selectedOption)
        if (selectedMonth && selectedOption) {
            loadMonthlyRota(selectedMonth.value, selectedOption.value).then(response => {
                const rotaData = response['body']
                setRota(rotaData)
                setOriginalRota(JSON.parse(JSON.stringify(rotaData)))
                setMonthYear({
                    month: rotaData['month'],
                    year: rotaData['year'],
                    monthName: rotaData['month_name']
                })
                setIsRotaEnabled(rotaData['enabled'] || false)
                setHasUnsavedChanges(false)
            }).catch((err) => {
                toast.error("Network error: " + err)
            })
        }
    }

    const handleEventSelection = (selectedOption) => {
        setSelectedEvent(selectedOption)
        if (selectedOption) {
            loadEventRota(selectedOption.value).then(response => {
                const eventData = response['body']

                // Check if the event has a rota
                if (!eventData || !eventData.rota_id) {
                    toast("This event does not have a rota yet")
                    setRota(null)
                    setIsRotaEnabled(false)
                    return
                }

                // API returns roles as a flat object { roleName: [...users] }
                // Wrap into a single services entry so the render code works the same as monthly
                const rotaData = {
                    id: eventData.rota_id,
                    enabled: eventData.enabled || false,
                    event_name: eventData.event_name,
                    event_date: eventData.event_date,
                    formatted_date: eventData.formatted_date,
                    services: [{
                        rota_id: eventData.rota_id,
                        roles: eventData.roles || {}
                    }]
                }

                setRota(rotaData)
                setOriginalRota(JSON.parse(JSON.stringify(rotaData)))
                setIsRotaEnabled(rotaData.enabled || false)
                setHasUnsavedChanges(false)
            }).catch((err) => {
                toast.error("Network error: " + err)
            })
        }
    }

    const handleUserChange = (sundayIndex, serviceIndex, roleName, userIndex, newUser) => {
        const updatedRota = JSON.parse(JSON.stringify(rota))
        if (viewType === 'monthly') {
            updatedRota.sundays[sundayIndex].services[serviceIndex].roles[roleName][userIndex] = newUser
        } else {
            updatedRota.services[serviceIndex].roles[roleName][userIndex] = newUser
        }
        setRota(updatedRota)
        setHasUnsavedChanges(true)
    }

    const handleAddUser = (sundayIndex, serviceIndex, roleName) => {
        const updatedRota = JSON.parse(JSON.stringify(rota))

        // Get the first available (non-absent) user for that date as default
        const date = sundayIndex !== null ? rota.sundays[sundayIndex].date : rota.event_date
        const absentIds = getAbsentUserIdsForDate(date)
        const isPhotography = roleName.toLowerCase() === 'photography'
        const pool = getUserPool(roleName)
        const availableUsers = pool
            .filter(u => !absentIds.has(u.id))
            .sort((a, b) => `${a.firstname} ${a.lastname}`.localeCompare(`${b.firstname} ${b.lastname}`))
        const defaultUser = availableUsers[0] || pool[0]

        if (!defaultUser) {
            toast.error(isPhotography
                ? "No photography team members are available for this date."
                : "No team members are available at this location for this date.")
            return
        }

        if (viewType === 'monthly') {
            if (!updatedRota.sundays[sundayIndex].services[serviceIndex].roles[roleName]) {
                updatedRota.sundays[sundayIndex].services[serviceIndex].roles[roleName] = []
            }
            updatedRota.sundays[sundayIndex].services[serviceIndex].roles[roleName].push(defaultUser)
        } else {
            if (!updatedRota.services[serviceIndex].roles[roleName]) {
                updatedRota.services[serviceIndex].roles[roleName] = []
            }
            updatedRota.services[serviceIndex].roles[roleName].push(defaultUser)
        }

        setRota(updatedRota)
        setHasUnsavedChanges(true)
    }

    const handleRemoveUser = (sundayIndex, serviceIndex, roleName, userIndex) => {
        // const confirmed = window.confirm("Are you sure you want to remove this person from the role?")
        // if (!confirmed) return

        const updatedRota = JSON.parse(JSON.stringify(rota))

        if (viewType === 'monthly') {
            updatedRota.sundays[sundayIndex].services[serviceIndex].roles[roleName].splice(userIndex, 1)
        } else {
            updatedRota.services[serviceIndex].roles[roleName].splice(userIndex, 1)
        }

        setRota(updatedRota)
        setHasUnsavedChanges(true)
    }

    const getAbsentUserIdsForDate = (date) => {
        const targetDate = moment(date).startOf('day')
        return new Set(
            absences
                .filter(a => {
                    const start = moment(a['start_date']).startOf('day')
                    const end = moment(a['end_date']).startOf('day')
                    return targetDate.isBetween(start, end, 'day', '[]')
                })
                .map(a => a['user_id'])
        )
    }

    // Monthly rotas only offer people based at the rota's location; event rotas and the
    // photography team (who cover across locations) offer everyone.
    // Backend as_dict() stringifies ids, so compare as strings.
    const getUserPool = (roleName) => {
        if (roleName.toLowerCase() === 'photography') {
            const normIds = photographyTeamIds.map(id => Number(id))
            return users.filter(u => normIds.includes(Number(u.id)))
        }
        if (viewType === 'monthly' && selectedLocation) {
            return users.filter(u => String(u['location.id']) === String(selectedLocation.value))
        }
        return users
    }

    const getUserOptionsForDate = (date) => {
        const absentIds = getAbsentUserIdsForDate(date)
        return getUserPool('')
            .filter(u => !absentIds.has(u.id))
            .sort((a, b) => `${a.firstname} ${a.lastname}`.localeCompare(`${b.firstname} ${b.lastname}`))
            .map(u => ({ value: u.id, label: `${u.firstname} ${u.lastname}` }))
    }

    const getPhotographyOptionsForDate = (date) => {
        const absentIds = getAbsentUserIdsForDate(date)
        return getUserPool('photography')
            .filter(u => !absentIds.has(u.id))
            .sort((a, b) => `${a.firstname} ${a.lastname}`.localeCompare(`${b.firstname} ${b.lastname}`))
            .map(u => ({ value: u.id, label: `${u.firstname} ${u.lastname}` }))
    }

    const handleTeamChange = (sundayIndex, serviceIndex, newTeam) => {
        const updatedRota = JSON.parse(JSON.stringify(rota))

        if (viewType === 'monthly') {
            updatedRota.sundays[sundayIndex].services[serviceIndex].canary_wharf_team = newTeam
        } else {
            updatedRota.services[serviceIndex].canary_wharf_team = newTeam
        }

        setRota(updatedRota)
        setHasUnsavedChanges(true)
    }

    const handleSaveRota = () => {
        if (viewType === 'monthly') {
            // For monthly rotas, we need to update each rota_id separately
            const savePromises = []

            rota.sundays.forEach(sunday => {
                sunday.services.forEach(service => {
                    const updates = []

                    // Build updates array from roles
                    Object.keys(service.roles).forEach(roleName => {
                        const roleUsers = service.roles[roleName]
                        const userIds = roleUsers.map(user => user.id)
                        const roleId = roleNameToIdMap[roleName]

                        if (roleId) {
                            updates.push({
                                role_id: roleId,
                                user_ids: userIds
                            })
                        }
                    })

                    const rotaUpdateData = {
                        rota_id: service.rota_id,
                        updates: updates,
                        team: service.canary_wharf_team || null
                    }

                    savePromises.push(rota_resources.update_rota(rotaUpdateData))
                })
            })

            setIsSaving(true)
            Promise.all(savePromises).then((responses) => {
                setIsSaving(false)
                const hasError = responses.some(response => response.error && response.error.length > 0)
                if (hasError) {
                    const errorMessages = responses
                        .filter(response => response.error && response.error.length > 0)
                        .map(response => response.error)
                        .join(', ')
                    toast.error("Failed to save rota: " + errorMessages)
                } else {
                    toast.success("Rota saved successfully")
                    setOriginalRota(JSON.parse(JSON.stringify(rota)))
                    setHasUnsavedChanges(false)
                }
            }).catch((err) => {
                setIsSaving(false)
                toast.error("Failed to save rota: " + err)
            })
        } else {
            // For event rotas
            const savePromises = []

            rota.services.forEach(service => {
                const updates = []

                Object.keys(service.roles).forEach(roleName => {
                    const roleUsers = service.roles[roleName]
                    const userIds = roleUsers.map(user => user.id)
                    const roleId = roleNameToIdMap[roleName]

                    if (roleId) {
                        updates.push({
                            role_id: roleId,
                            user_ids: userIds
                        })
                    }
                })

                const rotaUpdateData = {
                    rota_id: service.rota_id,
                    updates: updates,
                    team: service.canary_wharf_team || null
                }

                savePromises.push(rota_resources.update_rota(rotaUpdateData))
            })

            setIsSaving(true)
            Promise.all(savePromises).then((responses) => {
                setIsSaving(false)
                const hasError = responses.some(response => response.error && response.error.length > 0)
                if (hasError) {
                    const errorMessages = responses
                        .filter(response => response.error && response.error.length > 0)
                        .map(response => response.error)
                        .join(', ')
                    toast.error("Failed to save rota: " + errorMessages)
                } else {
                    toast.success("Rota saved successfully")
                    setOriginalRota(JSON.parse(JSON.stringify(rota)))
                    setHasUnsavedChanges(false)
                }
            }).catch((err) => {
                setIsSaving(false)
                toast.error("Failed to save rota: " + err)
            })
        }
    }

    const handlePublishRota = () => {
        if (hasUnsavedChanges) {
            toast.error("Please save your changes before publishing the rota")
            return
        }

        const confirmed = window.confirm("Are you sure you want to publish this rota? This will make it visible to all users.")
        if (!confirmed) return

        const enablePromise = viewType === 'event'
            ? rota_resources.enable_event_rota(rota.id)
            : rota_resources.enable_rota(selectedMonth.value, selectedLocation.value)

        setIsPublishing(true)
        enablePromise.then(response => {
            setIsPublishing(false)
            if (response.error && response.error.length > 0) {
                toast.error("Failed to publish rota: " + response.error)
            } else {
                toast.success("Rota published successfully")
                setIsRotaEnabled(true)
            }
        }).catch((err) => {
            setIsPublishing(false)
            toast.error("Failed to publish rota: " + err)
        })
    }

    const handleUnpublishRota = () => {
        const confirmed = window.confirm("Are you sure you want to unpublish this rota? It will be hidden from users until republished.")
        if (!confirmed) return

        const disablePromise = viewType === 'event'
            ? rota_resources.disable_event_rota(rota.id)
            : rota_resources.disable_rota(selectedMonth.value, selectedLocation.value)

        setIsUnpublishing(true)
        disablePromise.then(response => {
            setIsUnpublishing(false)
            if (response.error && response.error.length > 0) {
                toast.error("Failed to unpublish rota: " + response.error)
            } else {
                toast.success("Rota unpublished successfully")
                setIsRotaEnabled(false)
            }
        }).catch((err) => {
            setIsUnpublishing(false)
            toast.error("Failed to unpublish rota: " + err)
        })
    }

    const exportToExcel = () => {
        if (!rota || !rota.sundays) return

        // Collect all unique role names across all sundays (preserving order, Producer first)
        const roleSet = new Set()
        rota.sundays.forEach(sunday => {
            sunday.services.forEach(service => {
                Object.keys(service.roles).forEach(r => roleSet.add(r))
            })
        })
        const roleNames = Array.from(roleSet).sort((a, b) => {
            if (a === 'Producer') return -1
            if (b === 'Producer') return 1
            return a.localeCompare(b)
        })

        // Build header row: "Role" + one column per Sunday+Service
        const headers = ['Role']
        rota.sundays.forEach(sunday => {
            sunday.services.forEach(service => {
                const dateLabel = moment(sunday.date, 'YYYY-MM-DD').format('DD MMM YYYY')
                headers.push(`${dateLabel}\n${service.service_name}`)
            })
        })

        // Build data rows — one row per role
        const rows = [headers]
        roleNames.forEach(roleName => {
            const row = [roleName]
            rota.sundays.forEach(sunday => {
                sunday.services.forEach(service => {
                    const usersInRole = service.roles[roleName] || []
                    row.push(usersInRole.map(u => `${u.firstname} ${u.lastname}`).join('\n'))
                })
            })
            rows.push(row)
        })

        const ws = XLSX.utils.aoa_to_sheet(rows)

        // Column widths
        ws['!cols'] = headers.map((_, i) => ({ wch: i === 0 ? 22 : 28 }))

        // Row heights (to fit wrapped names)
        ws['!rows'] = rows.map(() => ({ hpt: 50 }))

        const wb = XLSX.utils.book_new()
        const sheetName = `${monthYear.monthName} ${monthYear.year}`
        XLSX.utils.book_append_sheet(wb, ws, sheetName)

        const filename = `${monthYear.monthName}_${monthYear.year}_Rota${selectedLocation ? '_' + selectedLocation.label.replace(/ /g, '_') : ''}.xlsx`
        XLSX.writeFile(wb, filename)
    }

    const exportEventToExcel = () => {
        if (!rota || !rota.services) return

        const roleNames = Object.keys(rota.services[0]?.roles || {})

        // Header: Role + one column per service (usually just one for events)
        const headers = ['Role', ...rota.services.map((_, i) => `Assignment${rota.services.length > 1 ? ' ' + (i + 1) : ''}`)]

        const rows = [headers]
        roleNames.forEach(roleName => {
            const row = [roleName]
            rota.services.forEach(service => {
                const usersInRole = service.roles[roleName] || []
                row.push(usersInRole.map(u => `${u.firstname} ${u.lastname}`).join('\n'))
            })
            rows.push(row)
        })

        const ws = XLSX.utils.aoa_to_sheet(rows)
        ws['!cols'] = headers.map((_, i) => ({ wch: i === 0 ? 22 : 28 }))
        ws['!rows'] = rows.map(() => ({ hpt: 50 }))

        const wb = XLSX.utils.book_new()
        const eventLabel = selectedEvent ? selectedEvent.label.replace(/[^\w\s]/g, '').replace(/ /g, '_') : 'Event'
        XLSX.utils.book_append_sheet(wb, ws, eventLabel.substring(0, 31))
        XLSX.writeFile(wb, `${eventLabel}_Rota.xlsx`)
    }

    useEffect(() => {
        loadLocations().then(response => {
            setLocations(response['body'])
        }).catch((err) => {
            toast.error("Network error: " + err)
        })

        loadRotaDates().then(response => {
            const dates = response['body'][0]['event_dates']
            setRotaDates(dates)

            // Get current and next month only
            const currentMonth = moment()
            const nextMonth = moment().add(1, 'month')

            const months = {}
            dates.forEach(date => {
                const parsedDate = moment(date, "DD-MM-YYYY HH:mm:ss")
                const monthKey = parsedDate.format('YYYY-MM')

                // Only include current and next month
                if (parsedDate.isSame(currentMonth, 'month') || parsedDate.isSame(nextMonth, 'month')) {
                    const monthLabel = parsedDate.format('MMMM-YYYY') + ' Rota'
                    if (!months[monthKey]) {
                        months[monthKey] = {
                            value: parsedDate.format('DD-MM-YYYY HH:mm:ss'),
                            label: monthLabel,
                            month: parsedDate.month() + 1,
                            year: parsedDate.year()
                        }
                    }
                }
            })
            setAvailableMonths(Object.values(months))
        }).catch((err) => {
            toast.error("Network error: " + err)
        })

        loadEventRotas().then(response => {
            setEvents(response['body']['event_rotas'] || [])
        }).catch((err) => {
            toast.error("Network error: " + err)
        })

        loadUsers().then(response => {
            setUsers(response['body'])
        }).catch((err) => {
            toast.error("Network error: " + err)
        })

        loadRotaRoles().then(response => {
            const roles = response['body']
            setRotaRoles(roles)

            // Create a map from role name to role ID
            const nameToIdMap = {}
            roles.forEach(role => {
                nameToIdMap[role.name] = role.id
            })
            setRoleNameToIdMap(nameToIdMap)
        }).catch((err) => {
            toast.error("Network error: " + err)
        })

        absenceResources.get().then(response => {
            setAbsences(response['body'] || [])
        }).catch(() => {})

        configResources.get_by_name('PHOTOGRAPHY_TEAM_USER_IDS').then(response => {
            const config = response['body']
            if (config && config.value !== undefined) {
                // as_dict() already parses the JSON value, so it arrives as an array
                const ids = Array.isArray(config.value) ? config.value : []
                setPhotographyTeamIds(ids)
            }
        }).catch(() => {})
    }, [])

    const currentMonth = moment()
    const nextMonth = moment().add(1, 'month')
    const crossesYear = currentMonth.year() !== nextMonth.year()
    const upcomingEventRotas = events
        .filter(eventRota => {
            const eventDate = moment(eventRota.event_date)
            const isCurrentMonth = eventDate.month() === currentMonth.month() && eventDate.year() === currentMonth.year()
            const isNextMonth = eventDate.month() === nextMonth.month() && eventDate.year() === nextMonth.year()
            return isCurrentMonth || isNextMonth
        })
        .map(eventRota => ({
        value: eventRota.event_id,
        label: `${eventRota.event_name} - ${moment(eventRota.event_date).format('DD MMM YYYY')}`,
        rota_id: eventRota.rota_id,
        location_id: eventRota.location_id,
        service_id: eventRota.service_id,
        service_name: eventRota.service_name,
        team: eventRota.team,
        event_date: eventRota.event_date,
        formatted_date: eventRota.formatted_date,
        enabled: eventRota.enabled
    }))

    // Filter locations that use a rota
    const rotaLocations = locations
        .filter(loc => loc['uses_rota'] === "True" || loc['uses_rota'] === true || loc['uses_rota'] === 1)
        .map(loc => ({
            value: loc['id'],
            label: loc['name']
        }))
    

    return(
        <Fragment>
            <Topbar/>
            {(isPublishing || isSaving) && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0,49,101,0.85)',
                    zIndex: 9999,
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    color: '#fff'
                }}>
                    <div style={{
                        width: '60px', height: '60px',
                        border: '6px solid rgba(255,168,0,0.3)',
                        borderTop: '6px solid #ffa800',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginBottom: '20px'
                    }} />
                    {isPublishing && <>
                        <h3 style={{margin: 0, color: '#ffa800'}}>Publishing Rota...</h3>
                        <p style={{marginTop: '8px', color: '#ccc', fontSize: '14px'}}>Sending notifications to team members, please wait.</p>
                    </>}
                    {isSaving && <>
                        <h3 style={{margin: 0, color: '#ffa800'}}>Saving Rota...</h3>
                        <p style={{marginTop: '8px', color: '#ccc', fontSize: '14px'}}>Saving your changes, please wait.</p>
                    </>}
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                </div>
            )}

            <div className="hero-area">
            <div className="page-banner parallax" style={{backgroundColor:"#003165"}}>
                    <div className="container">                    
                        <div className="page-banner-text">
                            {isLoggedIn ?
                                <h1 style={{color:"#ffa800"}} className="block-title"><strong>Manage Rota</strong></h1>
                                :
                                <h1 style={{color:"#ffa800"}} className="block-title"><strong>{ACCESS_DENIED}</strong></h1>
                            }                            
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
            {
                isLoggedIn ?
        	<>
                <div className="container">

                <div className="spacer-40"></div>

                <div>
                    <div className="row">
                        <div className="col-md-12">
                            <h3 style={{color:"#ffa800"}}><strong>Rota Type</strong></h3>
                            <div className="spacer-20"></div>
                            <div>
                                <label style={{marginRight: "20px"}}>
                                    <input
                                        type="radio"
                                        value="monthly"
                                        checked={viewType === 'monthly'}
                                        onChange={handleViewTypeChange}
                                        style={{marginRight: "5px"}}
                                    />
                                    Monthly Rota
                                </label>
                                <label>
                                    <input
                                        type="radio"
                                        value="event"
                                        checked={viewType === 'event'}
                                        onChange={handleViewTypeChange}
                                        style={{marginRight: "5px"}}
                                    />
                                    Events Rota
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="spacer-40"></div>

                    {viewType === 'monthly' ? (
                        <div className="row">
                            <div className="col-md-6">
                                <label style={{color:"#ffa800"}}><strong>Select Month</strong></label>
                                <Select
                                    name='month'
                                    id="month"
                                    classNamePrefix='select-css-tag'
                                    value={selectedMonth}
                                    onChange={handleMonthSelection}
                                    options={availableMonths}
                                    placeholder="Select Month"
                                />
                            </div>

                            <div className="col-md-6">
                                <label style={{color:"#ffa800"}}><strong>Select Location</strong></label>
                                <Select
                                    name='location'
                                    id="location"
                                    classNamePrefix='select-css-tag'
                                    value={selectedLocation}
                                    onChange={handleLocationSelection}
                                    options={rotaLocations}
                                    placeholder="Select Location"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="row">
                            <div className="col-md-6">
                                <label style={{color:"#ffa800"}}><strong>Select Event ({currentMonth.format(crossesYear ? 'MMMM YYYY' : 'MMMM')} & {nextMonth.format('MMMM YYYY')})</strong></label>
                                <Select
                                    name='event'
                                    id="event"
                                    classNamePrefix='select-css-tag'
                                    value={selectedEvent}
                                    onChange={handleEventSelection}
                                    options={upcomingEventRotas}
                                    placeholder="Select Event"
                                />
                            </div>
                        </div>
                    )}
                </div>    

                <div className="spacer-40"></div>

                {rota && (
                    <>
                        <div style={{marginBottom: '20px'}}>
                            {viewType === 'event' && (
                                <div style={{marginBottom: '20px'}}>
                                    <h3 style={{color:"#003165"}}>
                                        <strong>{rota.event_name}</strong>
                                    </h3>
                                    <p style={{color:"#666"}}>
                                        {moment(rota.event_date).format('DD MMMM YYYY, HH:mm')}
                                    </p>
                                </div>
                            )}

                            {viewType === 'monthly' && monthYear.monthName && (
                                <div style={{marginBottom: '20px'}}>
                                    <h3 style={{color:"#003165"}}>
                                        <strong>{monthYear.monthName} {monthYear.year} Rota</strong>
                                    </h3>
                                </div>
                            )}

                            <button
                                onClick={handleSaveRota}
                                disabled={!hasUnsavedChanges}
                                style={{
                                    backgroundColor: hasUnsavedChanges ? '#ffa800' : '#ccc',
                                    color: '#fff',
                                    padding: '10px 20px',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: hasUnsavedChanges ? 'pointer' : 'not-allowed',
                                    marginRight: '10px',
                                    fontSize: '16px',
                                    fontWeight: 'bold'
                                }}
                            >
                                Save Changes
                            </button>
                            <button
                                onClick={handlePublishRota}
                                disabled={isPublishing}
                                style={{
                                    backgroundColor: isPublishing ? '#ccc' : '#003165',
                                    color: '#fff',
                                    padding: '10px 20px',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: isPublishing ? 'not-allowed' : 'pointer',
                                    fontSize: '16px',
                                    fontWeight: 'bold'
                                }}
                            >
                                {isPublishing ? 'Publishing...' : isRotaEnabled ? 'Rota Published' : 'Publish'}
                            </button>
                            {isRotaEnabled && (
                                <button
                                    onClick={handleUnpublishRota}
                                    disabled={isUnpublishing}
                                    style={{
                                        backgroundColor: isUnpublishing ? '#ccc' : '#dc3545',
                                        color: '#fff',
                                        padding: '10px 20px',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: isUnpublishing ? 'not-allowed' : 'pointer',
                                        marginLeft: '10px',
                                        fontSize: '16px',
                                        fontWeight: 'bold'
                                    }}
                                >
                                    {isUnpublishing ? 'Unpublishing...' : 'Unpublish'}
                                </button>
                            )}
                            <button
                                onClick={viewType === 'monthly' ? exportToExcel : exportEventToExcel}
                                style={{
                                    backgroundColor: '#28a745',
                                    color: '#fff',
                                    padding: '10px 20px',
                                    border: 'none',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    marginLeft: '10px',
                                    fontSize: '16px',
                                    fontWeight: 'bold'
                                }}
                            >
                                <i className="fa fa-download" style={{marginRight: '6px'}}></i>
                                Export to Excel
                            </button>
                        </div>
                    </>
                )}
                </div>

                {viewType === 'monthly' && rota && rota.sundays ? (
                    <div style={{
                        overflowX: 'auto',
                        WebkitOverflowScrolling: 'touch',
                        width: '100%'
                    }}>
                                <TableContainer component={Paper} style={{width: '100%'}}>
                                <Table stickyHeader style={{tableLayout: 'auto'}}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{
                                                minWidth: '150px',
                                                width: '180px',
                                                backgroundColor: '#003165',
                                                color: '#fff',
                                                fontWeight: 'bold',
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 3,
                                                borderRight: '1px solid #d7dde5',
                                                borderBottom: '2px solid #003165',
                                                fontSize: '13px',
                                                letterSpacing: '0.4px',
                                                boxShadow: '2px 0 6px rgba(0,0,0,0.08)'
                                            }}>
                                                Rota Roles
                                            </TableCell>
                                            {rota.sundays.map((sunday, sundayIndex) => (
                                                <React.Fragment key={sundayIndex}>
                                                    {sunday.services.map((service, serviceIndex) => (
                                                        <TableCell key={`${sundayIndex}-${serviceIndex}`} style={{
                                                            minWidth: '280px',
                                                            width: '320px',
                                                            backgroundColor: '#ffa800',
                                                            color: '#fff',
                                                            fontWeight: 'bold',
                                                            textAlign: 'center',
                                                            borderRight: '1px solid #d7dde5',
                                                            borderBottom: '2px solid #003165',
                                                            fontSize: '13px',
                                                            letterSpacing: '0.4px'
                                                        }}>
                                                            <div>
                                                                <div style={{marginBottom: '4px'}}>
                                                                    {moment(sunday.date, "YYYY-MM-DD").format('DD MMM')}
                                                                </div>
                                                                <div style={{fontSize: '0.85em', opacity: 0.9}}>
                                                                    {service.service_name}
                                                                </div>
                                                                {selectedLocation && selectedLocation.label.toLowerCase().includes('canary wharf') && (
                                                                    <div style={{marginTop: '6px'}}>
                                                                        <input
                                                                            type="text"
                                                                            placeholder="Team"
                                                                            value={service.canary_wharf_team || ''}
                                                                            onChange={(e) => handleTeamChange(sundayIndex, serviceIndex, e.target.value)}
                                                                            style={{
                                                                                width: '90%',
                                                                                padding: '4px 6px',
                                                                                fontSize: '0.8em',
                                                                                border: '1px solid #fff',
                                                                                borderRadius: '4px',
                                                                                backgroundColor: 'rgba(255,255,255,0.9)',
                                                                                color: '#003165'
                                                                            }}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </TableCell>
                                                    ))}
                                                </React.Fragment>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rota.sundays[0]?.services[0]?.roles && Object.keys(rota.sundays[0].services[0].roles).map((roleName, roleIndex) => (
                                            <TableRow key={roleName} style={{
                                                backgroundColor: roleIndex % 2 === 0 ? '#fff' : '#f8f9fa'
                                            }}>
                                                <TableCell style={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    backgroundColor: roleIndex % 2 === 0 ? '#fff' : '#f8f9fa',
                                                    zIndex: 2,
                                                    borderRight: '1px solid #d7dde5',
                                                    boxShadow: '2px 0 6px rgba(0,0,0,0.08)',
                                                    fontWeight: 'bold',
                                                    color: '#003165'
                                                }}>
                                                    {roleName}
                                                </TableCell>
                                                {rota.sundays.map((sunday, sundayIndex) => (
                                                    <React.Fragment key={sundayIndex}>
                                                        {sunday.services.map((service, serviceIndex) => (
                                                            <TableCell key={`${sundayIndex}-${serviceIndex}`} style={{
                                                                verticalAlign: 'top',
                                                                padding: '12px',
                                                                borderRight: '1px solid #e3e7ec'
                                                            }}>
                                                                <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                                                    {service.roles[roleName] && service.roles[roleName].map((user, userIndex) => (
                                                                        <div key={userIndex} style={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: '8px',
                                                                            backgroundColor: '#f9fafb',
                                                                            padding: '6px',
                                                                            borderRadius: '10px',
                                                                            border: '1px solid #e3e7ec'
                                                                        }}>
                                                                            <img
                                                                                src={user['image']}
                                                                                style={{
                                                                                    width: "40px",
                                                                                    height: "40px",
                                                                                    borderRadius: '50%',
                                                                                    objectFit: 'cover',
                                                                                    border: '2px solid #ffa800',
                                                                                    flexShrink: 0,
                                                                                    backgroundColor: '#e9edf2'
                                                                                }}
                                                                                alt={`${user.firstname} ${user.lastname}`}
                                                                            />
                                                                            <div style={{flex: 1, minWidth: '170px'}}>
                                                                                <Select
                                                                                    value={{
                                                                                        value: user.id,
                                                                                        label: `${user.firstname} ${user.lastname}`
                                                                                    }}
                                                                                    onChange={(selectedOption) => {
                                                                                        const selectedUser = users.find(u => u.id === selectedOption.value)
                                                                                        handleUserChange(sundayIndex, serviceIndex, roleName, userIndex, selectedUser)
                                                                                    }}
                                                                                    options={roleName.toLowerCase() === 'photography' ? getPhotographyOptionsForDate(sunday.date) : getUserOptionsForDate(sunday.date)}
                                                                                    classNamePrefix='select-css-tag'
                                                                                />
                                                                            </div>
                                                                            <button
                                                                                onClick={() => handleRemoveUser(sundayIndex, serviceIndex, roleName, userIndex)}
                                                                                style={{
                                                                                    backgroundColor: '#dc3545',
                                                                                    color: '#fff',
                                                                                    border: 'none',
                                                                                    borderRadius: '50%',
                                                                                    width: '22px',
                                                                                    height: '22px',
                                                                                    cursor: 'pointer',
                                                                                    fontSize: '16px',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    padding: 0,
                                                                                    flexShrink: 0
                                                                                }}
                                                                                title="Remove user"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                    <button
                                                                        onClick={() => handleAddUser(sundayIndex, serviceIndex, roleName)}
                                                                        style={{
                                                                            backgroundColor: '#28a745',
                                                                            color: '#fff',
                                                                            border: 'none',
                                                                            borderRadius: '6px',
                                                                            padding: '6px 10px',
                                                                            cursor: 'pointer',
                                                                            fontSize: '13px',
                                                                            fontWeight: 'bold',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            gap: '4px'
                                                                        }}
                                                                        title="Add user to this role"
                                                                    >
                                                                        + Add
                                                                    </button>
                                                                </div>
                                                            </TableCell>
                                                        ))}
                                                    </React.Fragment>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    ) : viewType === 'event' && rota && rota.services ? (
                        <div style={{
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            width: '100%',
                            maxWidth: '100%'
                        }}>
                                <TableContainer component={Paper} style={{width: '100%'}}>
                                <Table stickyHeader style={{tableLayout: 'auto'}}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell style={{
                                                minWidth: '150px',
                                                width: '180px',
                                                backgroundColor: '#003165',
                                                color: '#fff',
                                                fontWeight: 'bold',
                                                position: 'sticky',
                                                left: 0,
                                                zIndex: 3,
                                                borderRight: '1px solid #d7dde5',
                                                borderBottom: '2px solid #003165',
                                                fontSize: '13px',
                                                letterSpacing: '0.4px',
                                                boxShadow: '2px 0 6px rgba(0,0,0,0.08)'
                                            }}>
                                                Rota Roles
                                            </TableCell>
                                            {rota.services.map((service, serviceIndex) => (
                                                <TableCell key={serviceIndex} style={{
                                                    minWidth: '280px',
                                                    width: '320px',
                                                    backgroundColor: '#ffa800',
                                                    color: '#fff',
                                                    fontWeight: 'bold',
                                                    textAlign: 'center',
                                                    borderRight: '1px solid #d7dde5',
                                                    borderBottom: '2px solid #003165',
                                                    fontSize: '13px',
                                                    letterSpacing: '0.4px'
                                                }}>
                                                    <div>
                                                        <div>{service.service_name}</div>
                                                        {rota.location_id && locations.find(loc => loc.id === rota.location_id)?.name.toLowerCase().includes('canary wharf') && (
                                                            <div style={{marginTop: '6px'}}>
                                                                <input
                                                                    type="text"
                                                                    placeholder="Team"
                                                                    value={service.canary_wharf_team || ''}
                                                                    onChange={(e) => handleTeamChange(null, serviceIndex, e.target.value)}
                                                                    style={{
                                                                        width: '90%',
                                                                        padding: '4px 6px',
                                                                        fontSize: '0.8em',
                                                                        border: '1px solid #fff',
                                                                        borderRadius: '4px',
                                                                        backgroundColor: 'rgba(255,255,255,0.9)',
                                                                        color: '#003165'
                                                                    }}
                                                                />
                                                            </div>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rota.services[0]?.roles && Object.keys(rota.services[0].roles).map((roleName, roleIndex) => (
                                            <TableRow key={roleName} style={{
                                                backgroundColor: roleIndex % 2 === 0 ? '#fff' : '#f8f9fa'
                                            }}>
                                                <TableCell style={{
                                                    position: 'sticky',
                                                    left: 0,
                                                    backgroundColor: roleIndex % 2 === 0 ? '#fff' : '#f8f9fa',
                                                    zIndex: 2,
                                                    borderRight: '1px solid #d7dde5',
                                                    boxShadow: '2px 0 6px rgba(0,0,0,0.08)',
                                                    fontWeight: 'bold',
                                                    color: '#003165'
                                                }}>
                                                    {roleName}
                                                </TableCell>
                                                {rota.services.map((service, serviceIndex) => (
                                                    <TableCell key={serviceIndex} style={{
                                                        verticalAlign: 'top',
                                                        padding: '12px',
                                                        borderRight: '1px solid #e3e7ec'
                                                    }}>
                                                        <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
                                                            {service.roles[roleName] && service.roles[roleName].map((user, userIndex) => (
                                                                <div key={userIndex} style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '8px',
                                                                    backgroundColor: '#f9fafb',
                                                                    padding: '6px',
                                                                    borderRadius: '10px',
                                                                    border: '1px solid #e3e7ec'
                                                                }}>
                                                                    <img
                                                                        src={user['image']}
                                                                        style={{
                                                                            width: "40px",
                                                                            height: "40px",
                                                                            borderRadius: '50%',
                                                                            objectFit: 'cover',
                                                                            border: '2px solid #ffa800',
                                                                            flexShrink: 0,
                                                                            backgroundColor: '#e9edf2'
                                                                        }}
                                                                        alt={`${user.firstname} ${user.lastname}`}
                                                                    />
                                                                    <div style={{flex: 1, minWidth: '170px'}}>
                                                                        <Select
                                                                            value={{
                                                                                value: user.id,
                                                                                label: `${user.firstname} ${user.lastname}`
                                                                            }}
                                                                            onChange={(selectedOption) => {
                                                                                const selectedUser = users.find(u => u.id === selectedOption.value)
                                                                                handleUserChange(null, serviceIndex, roleName, userIndex, selectedUser)
                                                                            }}
                                                                            options={roleName.toLowerCase() === 'photography' ? getPhotographyOptionsForDate(rota.event_date) : getUserOptionsForDate(rota.event_date)}
                                                                            classNamePrefix='select-css-tag'
                                                                        />
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleRemoveUser(null, serviceIndex, roleName, userIndex)}
                                                                        style={{
                                                                            backgroundColor: '#dc3545',
                                                                            color: '#fff',
                                                                            border: 'none',
                                                                            borderRadius: '50%',
                                                                            width: '22px',
                                                                            height: '22px',
                                                                            cursor: 'pointer',
                                                                            fontSize: '16px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            padding: 0,
                                                                            flexShrink: 0
                                                                        }}
                                                                        title="Remove user"
                                                                    >
                                                                        ×
                                                                    </button>
                                                                </div>
                                                            ))}
                                                            <button
                                                                onClick={() => handleAddUser(null, serviceIndex, roleName)}
                                                                style={{
                                                                    backgroundColor: '#28a745',
                                                                    color: '#fff',
                                                                    border: 'none',
                                                                    borderRadius: '6px',
                                                                    padding: '6px 10px',
                                                                    cursor: 'pointer',
                                                                    fontSize: '13px',
                                                                    fontWeight: 'bold',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    gap: '4px'
                                                                }}
                                                                title="Add user to this role"
                                                            >
                                                                + Add
                                                            </button>
                                                        </div>
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>
                    ) : null}

                <div className="container">
                    <h3> <a style={{color:"#ffa800"}} href="https://docs.google.com/spreadsheets/d/1rK4UaLqlKfjmkWdAyJOPPCckwJkO8FlrRcDW5MEobwE/edit#gid=0" target="_blank">Full Month Rota</a></h3>
                </div>
            </>   :<></> 
               
            }          
          

            </div>

        </Fragment>
    )
}

const mapStateToProps = (state) => {
    return{
       isLoggedIn: state.authReducer.isLoggedIn,
       loggedInUser: state.authReducer.user
    };
    
};

const mapDispatchToProps = {
    logout
};

export default connect(mapStateToProps, mapDispatchToProps)(ManageRota);